import { Packs } from '@Renderer/Theme.js'
import { createContext } from '@Utils/Canvas.js'
import { Timer } from '@Utils/Timer.js'

let timer = new Timer()
const [patternCanvas, patternContext] = createContext(1, 1, 'Texture.patternCanvas')

async function getImage(url) {
	const response = await fetch(url)
	if (!response.ok) {
		console.error("failed to fetch " + url)
		throw new Error(`${response.status} ${response.statusText} ${url}`)
	}
	try {
		let res = await response.blob()
		return await createImageBitmap(res)
	} catch (error) {
		console.error(error, url)
		throw error
	}
}
async function scaleImage(image, targetWidth, targetHeight) {
	if(image.width <= 0 || image.height <= 0 || targetWidth <= 0 || targetHeight <= 0) {
		throw new Error("Invalid image dimensions for scaling",targetWidth,targetHeight)
	}
	const [canvas, context] = createContext(targetWidth, targetHeight, 'Texture.scaleImage')
	context.drawImage(image, 0, 0, targetWidth, targetHeight)
	return await createImageBitmap(canvas)
}
async function overlayImage(image, options) {
	let overlay = await getImage(options.image)
	let scale = options.scale ?? 1
	overlay = await scaleImage(overlay, image.width * scale, image.height * scale)
	const [canvas, context] = createContext(image.width, image.height, 'Texture.overlayImage')
	context.drawImage(image, 0, 0)
	let pattern = patternContext.createPattern(overlay, 'repeat')
	context.globalCompositeOperation = options.gco ?? 'source-over'
	context.globalAlpha = options.opacity ?? 1
	context.fillStyle = pattern
	context.fillRect(0, 0, image.width, image.height)
	return await createImageBitmap(canvas)
}
async function offsetImage(image, options) {
	const [canvas, context] = createContext(image.width, image.height, 'Texture.offsetImage')
	let offsetX = options.left * -1 ?? 0
	let offsetY = options.top * -1 ?? 0
	let width = image.width + options.left + options.right
	let height = image.height + options.top + options.bottom
	context.drawImage(image, offsetX, offsetY, width, height)
	return await createImageBitmap(canvas)
}
async function colorizeImage(image, options) {
	const [canvas, context] = createContext(image.width, image.height, 'Texture.colorizeImage')
	context.drawImage(image, 0, 0)
	context.globalCompositeOperation = options.gco ?? 'source-over'
	context.globalAlpha = options.opacity ?? 1
	context.fillStyle = options.color
	context.fillRect(0, 0, image.width, image.height)
	return await createImageBitmap(canvas)
}
async function changeOpacityWithResolution(image, resolution, background) {
	const [canvas, context] = createContext(image.width, image.height, 'Texture.alphaImage')
	context.fillStyle = options.background
	context.fillRect(0, 0, image.width, image.height)
	context.globalCompositeOperation = 'source-over'
	context.globalAlpha = resolution
	context.drawImage(image, 0, 0)
	return await createImageBitmap(canvas)
}
async function alphaImage(image, options) {
	const [canvas, context] = createContext(image.width, image.height, 'Texture.alphaImage')
	context.fillStyle = options.background
	context.fillRect(0, 0, image.width, image.height)
	if (options.opacity > 0) {
		context.globalCompositeOperation = options.gco ?? 'source-over'
		context.globalAlpha = options.opacity ?? 1
		context.drawImage(image, 0, 0)
	}
	return await createImageBitmap(canvas)
}
async function filterImage(image, options) {
	let filter = []
	if (options.saturate) { filter.push(`saturate(${options.saturate})`) }
	if (options.sepia) { filter.push(`sepia(${options.sepia})`) }
	if (options.hue) { filter.push(`hue-rotate(${options.hue})`) }
	if (options.brightness) { filter.push(`brightness(${options.brightness})`) }
	if (options.contrast) { filter.push(`contrast(${options.contrast})`) }
	if (options.grayscale) { filter.push(`grayscale(${options.grayscale})`) }
	if (options.invert) { filter.push(`invert(${options.invert})`) }
	if (options.blur) { filter.push(`blur(${options.blur})`) }
	if (options.opacity) { filter.push(`opacity(${options.opacity})`) }
	if (filter.length > 0) {
		const [canvas, context] = createContext(image.width, image.height, 'Texture.colorizeImage')
		context.filter = filter.join(' ')
		context.fillRect(0, 0, image.width, image.height)
		context.drawImage(image, 0, 0)
		return await createImageBitmap(canvas)
	}
	return image
}
async function getAverageRGB(image) {
	const [canvas, context] = createContext(1, 1, 'Texture.getAverageRGB')
	context.drawImage(image, 0, 0, 1, 1);
	return context.getImageData(0, 0, 1, 1).data.slice(0,3);
}
async function tileImage(image, options) {
	let scaleWidth = image.width / options.horizontal
	let scaleHeight = image.height / options.vertical
	let si = await scaleImage(image, scaleWidth, scaleHeight)
	const [canvas, context] = createContext(options.width, options.height, 'Texture.tileImage')
	context.fillStyle = patternContext.createPattern(si, 'repeat')
	context.fillRect(0, 0, options.width, options.height)
	return await createImageBitmap(canvas)
}
async function createTexture(url, compileOptions) {
	return await compile(compileOptions, await getImage(url))
}
async function compile(stages, image) {
	let result = image
	for (let stage of stages) {
		if (stage.disabled === true) {
			continue
		}
		switch (stage.name) {
			case 'offset': result = await offsetImage(result, stage); break
			case 'overlay': result = await overlayImage(result, stage); break
			case 'filter': result = await filterImage(result, stage); break
			case 'colorize': result = await colorizeImage(result, stage); break
			case 'alpha': result = await alphaImage(result, stage); break
			case 'tile': result = await tileImage(result, stage); break
		}
	}
	return result
}
export class Texture {
	name
	url
	size
	ready
	scaled = {}
	images = {}
	patterns = {}
	resolution = 1;
	constructor(name, options) {
		if (!options) {
			options = {}
		}
		let images = options.images
		if (!images) {
			images = ['normal', 'diffuse', 'ao', 'roughness', 'displacement']
		}
		let compileOptions = options.compileOptions ?? {}
		this.compileOptions = {}
		for (let key of images) {
			if (!this.compileOptions[key]) {
				this.compileOptions[key] = [
					...compileOptions.all ?? [],
					...compileOptions[key] ?? []
				]
			}
		}
		this.name = name
		this.url = options.url
		this.urls = {}
		for (let key of images) {
			let ext = 'jpg'
			if (key == 'clipping') {
				ext = 'jpg'
			}
			this.urls[key] = this.url + "/" + key + "." + ext
		}
		this.size = options.size ?? 25
		this.width = this.size
		this.height = this.size
		this.ready = false
	}
	async loadTexture() {
		this.images = {}
		for (let key in this.urls) {
			this.images[key] = await createTexture(this.urls[key], this.compileOptions[key])
		}
		this.ready = true
	}
	async adjustOpacity() {
		// res 0.32783433376128474 looks good, now what?
		let o = Math.min(1/this.resolution,1)
		for (let key in this.images) {
			if (key === 'diffuse') {
				continue
			}
			let bg = 'rgb(255,255,255)'
			if (key == 'displacement') {
				bg = 'rgb(128,128,128)'
			} else if (key == 'normal') {
				bg = 'rgb(128,128,255)'
			} else if (key == 'roughness') {
				bg = 'rgb(255,255,255)'
			} else if (key == 'ao') {
				bg = 'rgb(255,255,255)'
			} else if (key == 'metallic') {
				bg = 'rgb(255,255,255)'
			} else {
				continue
			}
			this.images[key] = await alphaImage(this.images[key], { opacity: o, background: bg },)
		}
	}
	async scale() {
		let s = this.getSize()
		// if (this.width === s && this.height === s) {
		// 	this.finalize()
		// 	return
		// }
		this.scaled = {}
		for (let key in this.images) {
			if (this.images[key]) {
				let image = this.images[key]
				if(s < 60) {
					let rgb = await getAverageRGB(image)
					let color = `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`
					image = await colorizeImage(image, { color: color, opacity: 1 - s/60 })
				}
				this.scaled[key] = await scaleImage(image, s, s)
			}
		}
		this.finalize()
	}

	getSize() {
		return Math.max(1, Math.round(this.size / this.resolution))
	}
	invalidate(resolution) {
		resolution = !resolution ? 1 : resolution
		if (this.resolution == resolution) {
			return
		}
		this.resolution = resolution
		this.ready = false
	}
	async finalize() {
		this.patterns = {}
		for (let key in this.scaled) {
			this.patterns[key] = patternContext.createPattern(this.scaled[key], 'repeat')
		}
		this.ready = true
	}
}

export class TextureProvider {
	ready = false;
	textures = {};
	resolution = 1;
	constructor(pack) {
		this.pack = Packs[pack] ?? Packs.park
		this.texturesLoaded = false
	}
	static defaultSize = {
		tree: 130,
		bush: 50
	}
	async resetTextures(pack) {
		this.textures = {}
		this.texturesLoaded = false
		this.resolution = null
		this.pack = Packs[pack] ?? Packs.park
		await this.loadTextures()
	}
	async loadTextures() {
		if (this.texturesLoaded === true) {
			return
		}
		timer.time('TextureProvider.loadTextures')
		let pack = this.pack
		for (let key in pack) {
			let texture = pack[key]
			await this.addTexture(key, texture)
		}
		this.texturesLoaded = true
		timer.timeEnd('TextureProvider.loadTextures')
	}
	getTexture(name) {
		return this.textures[name]
	}
	async invalidate(resolution, force) {
		resolution = !resolution ? 1 : resolution
		if (this.resolution == resolution || force) {
			return
		}
		this.textureResolution = resolution
		this.ready = false
		this.resolution = resolution

		this.displacementScale = Math.max(Math.min(100 / this.resolution, 350), 50)
		for (let t in this.textures) {
			let tex = this.textures[t]
			await tex.invalidate(this.resolution)
		}
		await this.scale()
		this.ready = true
	}
	async scale() {
		for (let t in this.textures) {
			let tex = this.textures[t]
			await tex.adjustOpacity()
			await tex.scale()
		}
	}
	async addTexture(name, options) {
		let tex = new Texture(name, options)
		this.textures[name] = tex
		await tex.loadTexture()
	}
	getDiffusePattern(name) {
		let tex = this.textures[name]
		if (!tex || !tex.ready) {
			return 'rgba(255,255,255,1)'
		}
		return this.textures[name].patterns.diffuse
	}
	getNormalPattern(name) {
		let tex = this.textures[name]
		if (!tex || !tex.ready) {
			return 'rgba(128,128,255,1)'
		}
		return this.textures[name].patterns.normal
	}
	getDisplacementPattern(name) {
		let tex = this.textures[name]
		if (!tex || !tex.ready) {
			return 'rgba(128,128,128,1)'
		}
		return this.textures[name].patterns.displacement
	}
	getRoughnessPattern(name) {
		let tex = this.textures[name]
		if (!tex || !tex.ready) {
			return 'rgba(255,255,255,1)'
		}
		return this.textures[name].patterns.roughness
	}
	getAoPattern(name) {
		let tex = this.textures[name]
		if (!tex || !tex.ready) {
			return 'rgba(255,255,255,1)'
		}
		return this.textures[name].patterns.ao
	}
	getMetallicPattern(name) {
		let tex = this.textures[name]
		if (!tex || !tex.ready) {
			return 'rgba(255,255,255,1)'
		}
		return this.textures[name].patterns.metallic
	}
	has(name) {
		return this.textures[name] !== undefined
	}
}